<script setup>
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { useApiAction } from '../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import InputText from '../../../../../../../resources/js/components/umisis/form/inputs/InputText.vue'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { useProcedureStore } from '../../stores/procedures.js'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'

const props = defineProps({
  businessSystemId: {
    type: [Number, String],
    required: true,
  },
})

const emit = defineEmits(['created'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const procedureStore = useProcedureStore()

const procedureName = ref('')
const procedureGoal = ref('')

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
const fieldControlClass = (fieldName) => ([
  fieldControlBaseClass,
  validationResult(fieldName) === false ? 'border-destructive focus-visible:ring-destructive bg-red-50' : '',
  validationResult(fieldName) === true ? 'border-success focus-visible:ring-success bg-green-50' : '',
])

const normalizedBusinessSystemId = computed(() => Number(props.businessSystemId))
const isSubmitDisabled = computed(() => {
  return loading.value || !Number.isFinite(normalizedBusinessSystemId.value) || normalizedBusinessSystemId.value <= 0
})

const resetFields = () => {
  procedureName.value = ''
  procedureGoal.value = ''
}

const { execute, loading, getError, validationResult } = useApiAction((payload) => {
  return procedureStore.createProcedure(payload)
}, {
  onSuccess: (procedure) => {
    toast.success('Procedure created successfully.')
    emit('created', procedure)
    closeModal()
  },
})

defineExpose({
  openModal,
  closeModal,
})

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  await execute({
    business_system_id: normalizedBusinessSystemId.value,
    title: procedureName.value,
    goal: procedureGoal.value || null,
  })
}
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Create Procedure"
    description="Start a new procedure with a name and goal."
    ok-title="Create"
    cancel-title="Cancel"
    :ok-disabled="isSubmitDisabled"
    @ok="handleOk"
    @show="resetFields"
  >
    <div class="flex flex-col gap-4">
      <InputText
        v-model="procedureName"
        label="Name"
        placeholder="Monthly Payroll Process"
        :validation="validationResult('title')"
        :error-message="getError('title')"
      />

      <Field>
        <FieldLabel for="procedure-goal">
          Goal
        </FieldLabel>
        <textarea
          id="procedure-goal"
          v-model="procedureGoal"
          rows="3"
          placeholder="What does this procedure need to accomplish?"
          :class="fieldControlClass('goal')"
        />
        <FieldDescription v-if="getError('goal')" class="text-destructive font-medium">
          {{ getError('goal') }}
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>
